/*
 * SEO metadata per route, per language.
 * Titles kept under 60 chars, descriptions under 160.
 */

const CLUB_NAMES = {
  'club-a': 'Club A',
  'club-b': 'Club B',
  'club-c': 'Club C',
  'club-d': 'Club D',
  'club-e': 'Club E',
};

const pages = {
  '/': {
    en: {
      title: 'YBV | Brand Intelligence for Soccer',
      description: 'We help clubs, federations, and rights holders uncover commercial value they are not capturing — across sponsorship, partnerships, and media rights.',
    },
    es: {
      title: 'YBV | Inteligencia de Marca para el Fútbol',
      description: 'Ayudamos a clubes, federaciones y titulares de derechos a detectar valor comercial no capturado — en patrocinios, partnerships y derechos audiovisuales.',
    },
  },
  '/club-demo': {
    en: {
      title: 'Club Brand Intelligence Demo | YBV',
      description: 'Interactive brand intelligence demo for soccer clubs. Explore brand value, commercial opportunity, and strategic benchmarking for La Liga clubs.',
    },
    es: {
      title: 'Demo Inteligencia de Marca | YBV',
      description: 'Demo interactiva de inteligencia de marca para clubes de fútbol. Valor de marca, oportunidad comercial y benchmarking estratégico para La Liga.',
    },
  },
  '/briefing': {
    en: {
      title: 'Request a Briefing | YBV Sports',
      description: 'Request a confidential brand intelligence briefing for your club or federation. Discover commercial value driven by brand strength.',
    },
    es: {
      title: 'Solicitar Briefing | YBV Sports',
      description: 'Solicita un briefing confidencial de inteligencia de marca para tu club o federación. Descubre valor comercial vinculado a marca.',
    },
  },
  '/privacy-policy': {
    en: {
      title: 'Privacy Policy | YBV Sports',
      description: 'YBV Sports privacy policy. How we collect, use, and protect your data.',
    },
    es: {
      title: 'Política de Privacidad | YBV Sports',
      description: 'Política de privacidad de YBV Sports. Cómo recogemos, usamos y protegemos tus datos.',
    },
  },
  '/terms-and-conditions': {
    en: {
      title: 'Terms & Conditions | YBV Sports',
      description: 'YBV Sports terms and conditions governing use of the platform and services.',
    },
    es: {
      title: 'Términos y Condiciones | YBV Sports',
      description: 'Términos y condiciones de uso de la plataforma y servicios de YBV Sports.',
    },
  },
  '/legal-notice': {
    en: {
      title: 'Legal Notice | YBV Sports',
      description: 'YBV Sports legal notice. Service provider identification and website terms under LSSI-CE.',
    },
    es: {
      title: 'Aviso Legal | YBV Sports',
      description: 'Aviso legal de YBV Sports. Identificación del titular y condiciones del sitio web conforme a la LSSI-CE.',
    },
  },
  '/login': {
    en: {
      title: 'Sign In | YBV Sports',
      description: 'Sign in to the YBV brand intelligence platform.',
    },
    es: {
      title: 'Iniciar Sesión | YBV Sports',
      description: 'Accede a la plataforma de inteligencia de marca YBV.',
    },
  },
};

/**
 * Returns SEO metadata for a given path, language, and optional search params.
 */
export function getSEOMeta(pathname, lang, searchParams) {
  const l = lang === 'es' ? 'es' : 'en';
  const altLang = l === 'es' ? 'en' : 'es';
  const base = window.location.origin;

  let meta = pages[pathname]?.[l] || pages['/'][l];
  let title = meta.title;
  let description = meta.description;

  // Club demo: override title with club name
  if (pathname === '/club-demo' && searchParams) {
    const clubId = searchParams.get('club');
    const clubName = CLUB_NAMES[clubId];
    if (clubName) {
      title = l === 'es'
        ? `${clubName}: Inteligencia de Marca | YBV`
        : `${clubName}: Brand Intelligence | YBV`;
    }
  }

  // Build canonical URL (current lang)
  const canonicalUrl = buildUrl(base, pathname, searchParams, l);
  // Build alternate URLs
  const enUrl = buildUrl(base, pathname, searchParams, 'en');
  const esUrl = buildUrl(base, pathname, searchParams, 'es');

  return {
    title,
    description,
    canonicalUrl,
    enUrl,
    esUrl,
    ogImage: `${base}/og-image.png`,
    ogLocale: l === 'es' ? 'es_ES' : 'en_US',
    ogLocaleAlt: l === 'es' ? 'en_US' : 'es_ES',
    lang: l,
    altLang,
  };
}

function buildUrl(base, pathname, searchParams, lang) {
  const url = new URL(pathname, base);
  // Preserve existing search params (e.g. ?club=club-a)
  if (searchParams) {
    for (const [key, val] of searchParams.entries()) {
      if (key !== 'lang') url.searchParams.set(key, val);
    }
  }
  // Only add ?lang= for non-default language
  if (lang === 'es') url.searchParams.set('lang', 'es');
  return url.toString();
}
